const getSelectedOptionValue = (wrapper) => {
  if (!wrapper) return '';
  const select = wrapper.querySelector('select');
  if (select) {
    const selected = select.options[select.selectedIndex];
    return selected ? selected.value : '';
  }

  const checked = wrapper.querySelector('input[type="radio"]:checked');
  return checked ? checked.value : '';
};

const getOptionWrappers = (rootElement, selector = '.product-form__input') => {
  // Variant JSON keeps Shopify's original option order, even if the picker is rendered in a different visual order.
  return Array.from(rootElement.querySelectorAll(selector))
    .map((wrapper, index) => {
      const position = Number(wrapper.dataset.optionPosition);
      return {
        wrapper,
        index: Number.isFinite(position) && position > 0 ? position - 1 : index
      };
    })
    .sort((a, b) => a.index - b.index)
    .map(({ wrapper }) => wrapper);
};

const BRAND_MAX_QUANTITY_MAP = {
  DIOR: 3,
  Jellycat: 3,
  Chanel: 3,
  LEGO: 3
};

if (customElements.get('quick-buy') === undefined) {

  customElements.define('quick-buy', class QuickBuy extends HTMLElement {
    constructor() {
      'use strict';
      super();

      this.processingModal = false;
      this.productData = null;
      this.button = this.querySelector('a[href*="modal"]');
      this.targetModalId = this.button.getAttribute('href').substring(1);
      this.quickBuyProductHandle = this.button.dataset.productHandle;
      this.button.addEventListener('click', this.onModalRequestOpen.bind(this))
      this.productHotspot = this.dataset.productHotspot;
      this.imageWidth = this.dataset.imageWidth;
    }

    async onModalRequestOpen(event) {
      event.preventDefault();
      if (this.processingModal === true) {
        return;
      }

      const isFashionContext = this.dataset.fashionContext === 'true';

      const modal = document.getElementById(this.targetModalId);

      if (modal) {
        modal.classList.toggle('product-modal--fashion', isFashionContext);
        Woolman.ModalsAndDrawers.showModalOrDrawer(this.targetModalId);
        return;
      };

      this.processingModal = true;

      document.querySelectorAll('[data-quick-buy-modal]').forEach((modal) => {
        modal.remove();
      })

      const rootUrl = window.routes.root == '/' ? '/' : window.routes.root + '/'; // if user has selected locale convert root url from /en-gb to /en-gb/
      const res = await fetch(`${rootUrl}products/${this.quickBuyProductHandle}?view=ajax-modal${this.productHotspot ? '-hotspot' : ''}`)
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const modalElement = doc.body.firstChild;
      document.body.appendChild(modalElement);
      modalElement.classList.toggle('product-modal--fashion', isFashionContext);

      // Apply image width from URL parameter if present
      if (this.imageWidth) {
        const cardMedia = modalElement.querySelector('[data-image-width]');
        if (cardMedia) {
          cardMedia.style.width = `${this.imageWidth}%`;
        }
      }

      Woolman.ModalsAndDrawers.showModalOrDrawer(this.targetModalId);

      this.modal = modalElement;
      this.hookEvents();
    }

    hookEvents() {
      this.setupColorDropdowns();
      this.setupSizeGuideOverlay();
      this.initCustomScrollbar();
      this.updateOptions();
      this.updateMasterId();
      this.hydrateInventories();
      this.updateQuantityLimits();

      const radiosElement = this.modal.querySelector('.product-modal__radios');
      const closeElement = this.modal.querySelector('[data-close]');
      const overlayElement = this.modal.querySelector('[data-overlay]') || this.modal.querySelector('[data-modal-overlay]');

      if (radiosElement) {
        radiosElement.addEventListener('change', this.onVariantChange.bind(this));
      }
      if (closeElement) {
        closeElement.addEventListener('click', (event) => {
          this.closeSizeGuideOverlay();
          Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent(event);
        });
      }
      if (overlayElement) {
        overlayElement.addEventListener('click', (event) => {
          this.closeSizeGuideOverlay();
          Woolman.ModalsAndDrawers.closeModalOrDrawerOrDrawerFromEvent(event);
        });
      }

      const productForm = document.getElementById(`product-form-${this.dataset.product}`);
      if (!productForm) {
        console.warn(`quick-buy: product form not found for product ${this.dataset.product}`);
        this.processingModal = false;
        return;
      }
      productForm.addEventListener('submit', this.handleSubmit.bind(this));

      const modalOpenedEvent = new CustomEvent('quickBuy:modalOpened', {
        detail: {
          productId: this.dataset.product,
          productUrl: `${window.shopUrl}${this.dataset.url}`
        }
      });
      document.dispatchEvent(modalOpenedEvent);

      // Send Nosto tracking event
      if (typeof nostojs !== 'undefined') {
        nostojs(api => {
          api.createRecommendationRequest()
            .setProducts([{ product_id: this.dataset.product }])
            .load();
        });
      }

      this.processingModal = false;
    }

    setupSizeGuideOverlay() {
      this.sizeGuideLayer = this.modal.querySelector('[data-size-guide-layer]');
      if (!this.sizeGuideLayer) return;

      this.sizeGuideOpeners = this.modal.querySelectorAll('[data-size-guide-open]');
      this.sizeGuideClosers = this.modal.querySelectorAll('[data-size-guide-close]');

      this.sizeGuideOpeners.forEach((opener) => {
        opener.addEventListener('click', this.openSizeGuideOverlay.bind(this));
      });

      this.sizeGuideClosers.forEach((closer) => {
        closer.addEventListener('click', this.closeSizeGuideOverlay.bind(this));
      });
    }

    openSizeGuideOverlay(event) {
      event.preventDefault();
      if (!this.modal || !this.sizeGuideLayer) return;

      this.modal.classList.add('is-size-guide-open');
      this.sizeGuideLayer.setAttribute('aria-hidden', 'false');
    }

    closeSizeGuideOverlay(event) {
      if (event) event.preventDefault();
      if (!this.modal || !this.sizeGuideLayer) return;

      this.modal.classList.remove('is-size-guide-open');
      this.sizeGuideLayer.setAttribute('aria-hidden', 'true');
    }

    setupColorDropdowns() {
      if (!this.modal.querySelector('.product-form__input--dropdown select')) return;
      if (typeof globalThis.ColorDropdownEnhancer !== 'function') return;

      this.colorDropdownEnhancer = new globalThis.ColorDropdownEnhancer(this.modal);
      this.colorDropdownEnhancer.init();
    }

    initCustomScrollbar() {
      const scrollContainer = this.modal.querySelector('[data-modal-scroll-area]')
        || this.modal.querySelector('.product-modal__scroll-area')
        || this.modal.querySelector('.product-modal__layout')
        || this.modal.querySelector('[data-modal-dialog]');
      if (!scrollContainer) return;

      const trackHost = scrollContainer.parentElement || this.modal;
      const track = document.createElement('div');
      track.className = 'product-modal__custom-scrollbar';
      const thumb = document.createElement('div');
      thumb.className = 'product-modal__custom-scrollbar-thumb';
      track.appendChild(thumb);
      trackHost.appendChild(track);

      const update = () => {
        const { scrollHeight, clientHeight, scrollTop } = scrollContainer;
        track.style.top = `${scrollContainer.offsetTop}px`;
        track.style.height = `${clientHeight}px`;

        if (scrollHeight <= clientHeight) {
          track.classList.remove('product-modal__custom-scrollbar--visible');
          return;
        }
        track.classList.add('product-modal__custom-scrollbar--visible');
        const trackHeight = track.clientHeight;
        const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
        const maxScroll = scrollHeight - clientHeight;
        const thumbTop = (scrollTop / maxScroll) * (trackHeight - thumbHeight);
        thumb.style.height = `${thumbHeight}px`;
        thumb.style.transform = `translateY(${thumbTop}px)`;
      };

      requestAnimationFrame(update);
      scrollContainer.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
    }

    onVariantChange() {
      this.updateOptions();
      this.updateMasterId();
      this.hydrateInventories();
      this.updateQuantityLimits();
      this.toggleAddButton(true, '', false);

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.updateVariantInput();
        this.renderProductInfo();
      }
    }

    updateOptions() {
      const wrappers = getOptionWrappers(this.modal);
      this.options = wrappers.map((wrapper) => getSelectedOptionValue(wrapper));
    }

    hydrateInventories() {
      if (!this.dataset.product) return;

      const inventoryDataNode = this.modal?.querySelector('[data-product-inventories-json]');
      if (!inventoryDataNode?.textContent.trim()) return;

      let inventoryData;
      try {
        inventoryData = JSON.parse(inventoryDataNode.textContent);
      } catch (error) {
        console.error('quick-buy inventory parse failed', error);
        return;
      }

      globalThis.inventories = globalThis.inventories || {};
      globalThis.inventories[this.dataset.product] = inventoryData;
    }

    getQuantityInputField() {
      return this.modal?.querySelector(`quantity-input [data-quantity-input-field][form="product-form-${this.dataset.product}"]`);
    }

    getQuantityRules(variant) {
      const resolvedVariant = variant || this.currentVariant;
      const inventoryData = globalThis.inventories?.[this.dataset.product]?.[resolvedVariant?.id];
      let rules;

      if (Woolman?.QuantityLimits?.getVariantRules) {
        rules = Woolman.QuantityLimits.getVariantRules(resolvedVariant, inventoryData);
      } else {
        rules = {
          min: 1,
          max: null,
          disabled: !resolvedVariant || resolvedVariant.available === false
        };
      }

      const productBrand = this.getProductData()?.vendor;
      const brandMaxQuantity = BRAND_MAX_QUANTITY_MAP[productBrand];
      if (!Number.isFinite(Number(brandMaxQuantity)) || Number(brandMaxQuantity) <= 0) {
        return rules;
      }

      const normalizedBrandMaxQuantity = Number(brandMaxQuantity);
      if (Number.isFinite(Number(rules.max)) && Number(rules.max) > 0) {
        rules.max = Math.min(Number(rules.max), normalizedBrandMaxQuantity);
      } else {
        rules.max = normalizedBrandMaxQuantity;
      }

      return rules;
    }

    updateQuantityLimits() {
      const quantityInput = this.getQuantityInputField();
      if (!quantityInput) return;

      const rules = this.getQuantityRules();
      if (Woolman?.QuantityLimits?.applyRulesToInput) {
        Woolman.QuantityLimits.applyRulesToInput(quantityInput, rules);
      }
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.product}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        if (this.modal.dataset.isPreorder == 'true') {
          addButtonText.textContent = globalThis.variantStrings.preOrder;
        } else {
          addButtonText.textContent = globalThis.variantStrings.addToCart;
        }
      }

      if (!modifyClass) return;
    }

    renderProductInfo() {
      const modalView = this.productHotspot ? 'ajax-modal-hotspot' : 'ajax-modal';

      fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&view=${modalView}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Quick buy product info request failed with status ${response.status}`);
          }

          return response.text();
        })
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html')
          const destination = this.modal.querySelector(`#price-${this.dataset.product}`);
          const source = html.getElementById(`price-${this.dataset.product}`);

          if (source && destination) destination.innerHTML = source.innerHTML;

          const price = this.modal.querySelector(`#price-${this.dataset.product}`);
          const destinationInventory = this.modal.querySelector('[data-modal-inventory] [data-inventory-status-content]');
          const sourceInventory = html.querySelector('[data-modal-inventory] [data-inventory-status-content]');
          const destinationButton = this.modal.querySelector(`#product-form-${this.dataset.product} [name="add"]`);
          const sourceButton = html.querySelector(`#product-form-${this.dataset.product} [name="add"]`);

          if (price) price.classList.remove('hidden');
          if (sourceInventory && destinationInventory) destinationInventory.innerHTML = sourceInventory.innerHTML;

          if (sourceButton && destinationButton) {
            destinationButton.innerHTML = sourceButton.innerHTML;
            destinationButton.toggleAttribute('disabled', sourceButton.hasAttribute('disabled'));
          } else {
            this.toggleAddButton(!this.currentVariant.available, globalThis.variantStrings.soldOut);
          }

          const inventoryRoot = this.modal.querySelector('[data-modal-inventory]');
          if (inventoryRoot) inventoryRoot.classList.remove('hidden');
        })
        .catch((error) => {
          console.error('quick-buy renderProductInfo failed', error);
          this.toggleAddButton(!this.currentVariant.available, globalThis.variantStrings.soldOut);
        });
    }

    updateMasterId() {
      const variants = this.getVariantData();

      if (!Array.isArray(variants) || variants.length === 0) {
        this.currentVariant = null;
        return;
      }

      if (!Array.isArray(this.options) || this.options.length === 0) {
        const selectedVariantId = Number(this.modal.querySelector(`#product-form-${this.dataset.product} input[name="id"]`)?.value);
        this.currentVariant = variants.find((variant) => variant.id === selectedVariantId) || variants[0];
        return;
      }

      this.currentVariant = variants.find((variant) => {
        return !variant.options.map((option, index) => {
          return this.options[index] === option;
        }).includes(false);
      });
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(`#product-form-${this.dataset.product}, #product-form-installment-${this.dataset.product}`);
      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    setUnavailable() {
      const button = document.getElementById(`product-form-${this.dataset.product}`);
      const addButton = button.querySelector('[name="add"]');
      const addButtonText = button.querySelector('[name="add"] > span');
      const price = this.modal.querySelector(`#price-${this.dataset.product}`);
      const inventoryRoot = this.modal.querySelector('[data-modal-inventory]');
      if (!addButton) return;
      addButtonText.textContent = window.variantStrings.unavailable;
      addButton.setAttribute('disabled', 'disabled');
      if (price) price.classList.add('hidden');
      if (inventoryRoot) inventoryRoot.classList.add('hidden');
    }

    getVariantData() {
      if (this.variantData) return this.variantData;

      const variantDataNode = this.modal.querySelector('modal-radios [type="application/json"]');
      if (variantDataNode?.textContent.trim()) {
        const parsedVariantData = JSON.parse(variantDataNode.textContent);
        this.variantData = Array.isArray(parsedVariantData) ? parsedVariantData : [];
        return this.variantData;
      }

      const productDataNode = this.modal.querySelector('[data-product-json]');
      if (productDataNode?.textContent.trim()) {
        const productData = JSON.parse(productDataNode.textContent);
        this.variantData = Array.isArray(productData?.variants) ? productData.variants : [];
        return this.variantData;
      }

      this.variantData = [];
      return this.variantData;
    }

    getProductData() {
      if (this.productData) return this.productData;

      const productDataNode = this.modal.querySelector('[data-product-json]');
      if (!productDataNode?.textContent.trim()) {
        this.productData = {};
        return this.productData;
      }

      try {
        this.productData = JSON.parse(productDataNode.textContent);
      } catch (error) {
        console.error('quick-buy product parse failed', error);
        this.productData = {};
      }

      return this.productData;
    }

    async handleSubmit() {
      this.closeSizeGuideOverlay();
      const overlay = this.modal.querySelector('[data-overlay]');
      overlay.click();
      Woolman.ModalsAndDrawers.closeModalOrDrawer(this.targetModalId);
    }
  })

  customElements.define('modal-radios', class ModalRadios extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('change', this.onVariantChange);
      this.expandSwatchesButton = this.querySelector('[data-modal-expand-swatches]');
      this.hideSwatchesButton = this.querySelector('[data-modal-hide-swatches]');
      if (this.expandSwatchesButton) {
        this.expandSwatchesButton.addEventListener('click', this.expandSwatches.bind(this));
      }
      if (this.hideSwatchesButton) {
        this.hideSwatchesButton.addEventListener('click', this.hideSwatches.bind(this));
      }

      this.updateOptionLayoutClasses();
    }

    onVariantChange(e) {
      this.updateOptions();
      this.updateOptionLayoutClasses();
    }

    updateOptions() {
      const wrappers = getOptionWrappers(this);
      this.options = wrappers.map((wrapper) => getSelectedOptionValue(wrapper));

      this.updateLegends();
    }

    updateLegends() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((fieldset) => {
        const legend = fieldset.querySelector('legend [data-variant-legend]');
        const selectedValue = getSelectedOptionValue(fieldset);
        if (legend && selectedValue) {
          legend.textContent = selectedValue;
        }
      });

      const dropdownInputs = Array.from(this.querySelectorAll('.product-form__input--dropdown'));
      dropdownInputs.forEach((dropdownInput) => {
        const legend = dropdownInput.querySelector('[data-variant-legend]');
        const selectedValue = getSelectedOptionValue(dropdownInput);
        if (legend && selectedValue) {
          legend.textContent = selectedValue;
        }
      });
    }

    updateOptionLayoutClasses() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset.product-form__input'));
      let hasManyOptions = false;

      fieldsets.forEach((fieldset) => {
        const tileLabels = fieldset.querySelectorAll("input[type='radio'] + label:not(.swatch)");
        const useFourColumnGrid = tileLabels.length > 5;

        fieldset.classList.toggle('product-form__input--tile-grid-4', useFourColumnGrid);
        if (useFourColumnGrid) {
          hasManyOptions = true;
        }
      });

      this.classList.toggle('modal-radios--many-options', hasManyOptions);
      const radiosBlock = this.closest('.product-modal__radios');
      if (radiosBlock) {
        radiosBlock.classList.toggle('product-modal__radios--many-options', hasManyOptions);
      }
    }

    expandSwatches() {
      const swatchesList = this.querySelector('[data-modal-swatches-list]');
      if (swatchesList) {
        this.expandSwatchesButton.classList.add('hidden');
        this.expandSwatchesButton.setAttribute('aria-expanded', 'false');

        this.hideSwatchesButton.classList.remove('hidden');
        this.hideSwatchesButton.setAttribute('aria-expanded', 'true');

        swatchesList.classList.remove('hidden');
        // Focus management: move focus to hide button for keyboard users so they can easily collapse
        if (document.activeElement === this.expandSwatchesButton) {
          this.hideSwatchesButton.focus();
        }
      }
    }

    hideSwatches() {
      const swatchesList = this.querySelector('[data-modal-swatches-list]');
      if (swatchesList) {
        this.hideSwatchesButton.classList.add('hidden');
        this.hideSwatchesButton.setAttribute('aria-expanded', 'true');

        this.expandSwatchesButton.classList.remove('hidden');
        this.expandSwatchesButton.setAttribute('aria-expanded', 'false');

        swatchesList.classList.add('hidden');
        // Focus management: return focus to expand button for keyboard users
        if (document.activeElement === this.hideSwatchesButton) {
          this.expandSwatchesButton.focus();
        }
      }
    }
  });

}